import { ArrowRight, Package, Clock, Zap, Shield } from 'lucide-react';
import { Helmet } from 'react-helmet-async';
import HomeNav from '../Home/HomeNav';
import HomeFooter from '../Home/HomeFooter';
import { getAllProducts } from '@/data/products/registry';
import ProductCard from './components/ProductCard';
import { createBreadcrumbSchema, createCollectionPageSchema, createOrganizationSchema } from '@/config/seo';
import { getCanonicalUrl } from '@/config/site';

const ProductsPage = () => {
  const products = getAllProducts();
  const canonicalUrl = getCanonicalUrl('/products');
  const structuredData = [
    createBreadcrumbSchema([
      { name: 'Home', url: getCanonicalUrl('/') },
      { name: 'Products', url: canonicalUrl },
    ]),
    createCollectionPageSchema(
      'Help Genie Products',
      'Explore Help Genie voice AI products for after-hours coverage, customer support, lead capture, and scalable business conversations.',
      canonicalUrl,
    ),
    createOrganizationSchema(),
  ];

  return (
    <div className="min-h-screen w-full flex flex-col bg-[#F9FAFC]">
      <Helmet>
        <title>Help Genie Products | Voice AI Products for Support, Sales and After-Hours</title>
        <meta
          name="description"
          content="Explore Help Genie products built for after-hours call handling, customer support, lead capture, and voice AI deployment across growing businesses."
        />
        <meta
          name="keywords"
          content="voice ai products, ai phone answering, after hours call handling, customer support automation, lead capture voice ai"
        />
        <link rel="canonical" href={canonicalUrl} />
        {structuredData.map((entry, index) => (
          <script key={`products-structured-data-${index}`} type="application/ld+json">
            {JSON.stringify(entry)}
          </script>
        ))}
      </Helmet>
      <HomeNav />

      <main className="flex-1 flex flex-col items-center justify-center w-full">
        {/* Hero Section with Background */}
        <section className="relative w-full min-h-[600px] flex items-center justify-center text-white">
          {/* Background */}
          <div className="absolute inset-0 z-0">
            <div className="absolute inset-0 bg-gradient-to-br from-purple-900 via-purple-800 to-blue-900" />
            <div className="absolute inset-0 bg-[url('/hero-background.webp')] bg-cover bg-center opacity-20" />
          </div>

          {/* Content */}
          <div className="relative z-20 w-full max-w-screen-xl mx-auto px-8 py-20 md:py-32">
            <div className="text-center">
              <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full mb-6">
                <Package className="w-5 h-5 text-purple-300" />
                <span className="text-sm font-medium text-purple-200">Help Genie Products</span>
              </div>
              
              <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold mb-6 leading-tight">
                Products Built for
                <br />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-400 via-pink-400 to-blue-400">
                  Real Business Needs
                </span>
              </h1>
              <p className="text-xl md:text-2xl text-gray-200 mb-10 max-w-3xl mx-auto">
                Complete Help Genie packages designed to solve specific challenges. 
                Each product comes fully loaded with everything you need.
              </p>

              {/* Value Props */}
              <div className="flex flex-wrap justify-center gap-4 mb-8">
                <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full">
                  <Clock className="w-5 h-5 text-purple-400" />
                  <span className="text-sm font-semibold">Quick Setup</span>
                </div>
                <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full">
                  <Zap className="w-5 h-5 text-purple-400" />
                  <span className="text-sm font-semibold">Ready to Use</span>
                </div>
                <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full">
                  <Shield className="w-5 h-5 text-purple-400" />
                  <span className="text-sm font-semibold">Enterprise Ready</span>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-6">
                <a
                  href="/signup"
                  className="px-8 py-3 bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white font-semibold rounded-lg shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-300 text-lg w-full sm:w-auto text-center"
                >
                  Get Started Free
                  <ArrowRight className="inline ml-2 h-5 w-5" />
                </a>
                <a
                  href="/live-agent/business-onboarding"
                  className="text-gray-200 hover:text-white font-medium text-lg transition-colors duration-300"
                >
                  Talk to a Genie &gt;
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Products Section */}
        <section className="py-20 md:py-32 bg-slate-50 w-full">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 mb-4">
                Our Products
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Each product is a complete solution with all the features you need, bundled together.
              </p>
              <div className="mt-5 flex flex-wrap justify-center gap-x-5 gap-y-3 text-sm">
                <a href="/help-genie-for-business-solutions" className="text-purple-600 hover:text-purple-700 transition-colors">
                  View solutions
                </a>
                <a href="/playbooks" className="text-purple-600 hover:text-purple-700 transition-colors">
                  Explore playbooks
                </a>
              </div>
            </div>

            {/* Product Cards */}
            <div className="space-y-6">
              {products.map((product) => (
                <ProductCard key={product.meta.id} product={product} />
              ))}
            </div>

            {/* Coming Soon Teaser */}
            {products.length === 1 && (
              <div className="mt-12 text-center">
                <div className="inline-block bg-white rounded-2xl shadow-lg p-8 max-w-2xl">
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">More Products Coming Soon</h3>
                  <p className="text-gray-600 mb-4">
                    We're building more specialized Help Genie products to solve your unique business challenges.
                  </p>
                  <a
                    href="/contact"
                    className="inline-flex items-center gap-2 text-purple-600 font-semibold hover:text-purple-700 transition-colors"
                  >
                    Tell us what you need
                    <ArrowRight className="w-4 h-4" />
                  </a>
                </div>
              </div>
            )}
          </div>
        </section>

        {/* Why Products Section */}
        <section className="py-20 md:py-32 bg-white w-full">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 mb-4">
                Why Choose a Help Genie Product?
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Pre-built, tested, and optimized for specific use cases.
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              <div className="text-center p-8">
                <div className="w-16 h-16 bg-gradient-to-br from-purple-100 to-blue-100 rounded-2xl flex items-center justify-center mx-auto mb-6">
                  <Clock className="w-8 h-8 text-purple-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Ready in Minutes</h3>
                <p className="text-gray-600">
                  Skip the setup complexity. Our products come pre-configured with best practices built in.
                </p>
              </div>

              <div className="text-center p-8">
                <div className="w-16 h-16 bg-gradient-to-br from-purple-100 to-blue-100 rounded-2xl flex items-center justify-center mx-auto mb-6">
                  <Package className="w-8 h-8 text-purple-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Complete Package</h3>
                <p className="text-gray-600">
                  Everything you need in one product—features, integrations, and workflows all included.
                </p>
              </div>

              <div className="text-center p-8">
                <div className="w-16 h-16 bg-gradient-to-br from-purple-100 to-blue-100 rounded-2xl flex items-center justify-center mx-auto mb-6">
                  <Zap className="w-8 h-8 text-purple-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Battle Tested</h3>
                <p className="text-gray-600">
                  Built from real customer needs and refined through thousands of conversations.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Bottom CTA */}
        <section className="relative w-full py-16 md:py-20 bg-gradient-to-r from-purple-600 to-blue-600 overflow-hidden">
          <div className="relative z-10 max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-6 max-w-3xl mx-auto">
              Ready to Get Started?
            </h2>
            <p className="text-xl text-purple-100 mb-10 max-w-2xl mx-auto">
              Choose a product that fits your needs and be up and running in minutes.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="/signup"
                className="px-8 py-4 bg-white text-purple-600 font-semibold rounded-lg shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-300 text-lg"
              >
                Start Free Trial
                <ArrowRight className="inline ml-2 h-5 w-5" />
              </a>
              <a
                href="/contact"
                className="px-8 py-4 border-2 border-white/50 hover:border-white text-white font-semibold rounded-lg hover:shadow-lg transform hover:-translate-y-0.5 transition-all duration-300 text-lg"
              >
                Contact Sales
              </a>
            </div>
          </div>
        </section>
      </main>

      <HomeFooter />
    </div>
  );
};

export default ProductsPage;
